import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { formatDateID, formatDateLong, todayWITA, STATUSES } from "@/lib/constants";
import { ChartBar, Printer, FileXls, FileCsv, FilePdf, MagnifyingGlass, FunnelSimple, Syringe } from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const inputCls =
  "w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-rose-400 focus:ring-2 focus:ring-rose-100";

const PAYMENT_STATUSES = ["Belum Bayar", "Menunggu Verifikasi", "DP Terbayar", "Lunas", "Tidak Perlu Bayar", "Refund Sebagian", "Refund Penuh"];

// Period presets -> [start, end]
function presetRange(preset) {
  const t = todayWITA();
  const [y, m] = t.split("-").map(Number);
  const iso = (d) => d.toISOString().slice(0, 10);
  const now = new Date(t + "T00:00:00");
  if (preset === "today") return [t, t];
  if (preset === "yesterday") { const d = new Date(now); d.setDate(d.getDate() - 1); return [iso(d), iso(d)]; }
  if (preset === "week") { const d = new Date(now); const wd = (d.getDay() + 6) % 7; d.setDate(d.getDate() - wd); return [iso(d), t]; }
  if (preset === "month") return [`${y}-${String(m).padStart(2, "0")}-01`, t];
  if (preset === "year") return [`${y}-01-01`, t];
  return [t, t];
}

function fmtCell(key, val) {
  if (val == null || val === "") return "-";
  if (["tanggal_kunjungan", "hpht", "hpl", "tanggal_lahir", "tanggal_lahir_bayi", "tanggal_imunisasi_terakhir"].includes(key)) return formatDateID(val);
  if (Array.isArray(val)) return val.join(", ");
  return String(val);
}

// Compute display value using item + column key (mirror of backend _cell_value for known keys)
function cellVal(item, key) {
  if (key === "usia") return item.usia_text || (item.usia ? `${item.usia} tahun` : "-");
  if (key === "usia_kehamilan") return item.usia_kehamilan_text || (item.usia_kehamilan_minggu ? `${item.usia_kehamilan_minggu} minggu` : "-");
  return fmtCell(key, item[key]);
}

export default function Reports() {
  const [mode, setMode] = useState("umum"); // "umum" | "imun"
  const [preset, setPreset] = useState("month");
  const [range, setRange] = useState(presetRange("month"));
  const [meta, setMeta] = useState({ categories: [], services: [], petugas: [], vaccines: [] });
  const [f, setF] = useState({ kategori: "", jenis_layanan: "", petugas: "", jenis_vaksin: "", status: "", payment_status: "" });
  const [data, setData] = useState(null);
  const [imun, setImun] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/reports/meta").then((r) => setMeta(r.data)).catch(() => {});
  }, []);

  const setPresetRange = (p) => { setPreset(p); if (p !== "custom") setRange(presetRange(p)); };

  const servicesForCat = useMemo(
    () => (f.kategori ? meta.services.filter((s) => s.kategori === f.kategori) : meta.services),
    [f.kategori, meta.services]
  );
  const imunServices = useMemo(
    () => meta.services.filter((s) => /imunisasi/i.test(s.kategori || "") || (s.booking_fields || []).includes("jenis_vaksin")),
    [meta.services]
  );

  const params = () => ({
    start: range[0], end: range[1],
    kategori: f.kategori || undefined,
    jenis_layanan: f.jenis_layanan || undefined,
    petugas: f.petugas || undefined,
    jenis_vaksin: f.jenis_vaksin || undefined,
    status: f.status || undefined,
    payment_status: f.payment_status || undefined,
  });
  const imunParams = () => ({
    start: range[0], end: range[1],
    jenis_layanan: f.jenis_layanan || undefined,
    jenis_vaksin: f.jenis_vaksin || undefined,
    status: f.status || undefined,
  });

  const load = async () => {
    if (!range[0] || !range[1]) return toast.error("Pilih periode tanggal");
    setLoading(true);
    try {
      if (mode === "imun") {
        const res = await api.get("/reports/imunisasi", { params: imunParams() });
        setImun(res.data);
      } else {
        const res = await api.get("/reports/reservations", { params: params() });
        setData(res.data);
      }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };

  const downloadTo = (path, filename, p, format) => {
    const token = localStorage.getItem("token");
    const qp = new URLSearchParams({ format });
    Object.entries(p).forEach(([k, v]) => { if (v) qp.append(k, v); });
    fetch(`${API}${path}?${qp.toString()}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { if (!r.ok) throw new Error("gagal"); return r.blob(); })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${filename}.${format === "xlsx" ? "xlsx" : format}`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast.error("Gagal mengunduh laporan"));
  };
  const download = (format) => downloadTo("/export/report", "Laporan_Reservasi", params(), format);
  const downloadImun = (format) => downloadTo("/export/imunisasi", "Laporan_Imunisasi", imunParams(), format);

  const perDay = useMemo(() => {
    if (!data) return [];
    const m = {};
    data.items.forEach((r) => { const k = r.tanggal_kunjungan || "-"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort().map(([k, v]) => ({ label: k === "-" ? "-" : k.slice(5), value: v }));
  }, [data]);
  const perKat = useMemo(() => {
    if (!data) return [];
    const m = {};
    data.items.forEach((r) => { const k = r.kategori_layanan || "Lainnya"; m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ label: k, value: v }));
  }, [data]);

  const columns = data?.columns || [];
  const imunColumns = imun?.columns || [];

  return (
    <div className="animate-fade-up space-y-6">
      <div className="no-print">
        <div className="flex items-center gap-2">
          <ChartBar size={26} weight="duotone" className="text-[#7C4DE0]" />
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-800">Laporan / Rekap Reservasi</h1>
        </div>
        <p className="mt-1 text-sm text-slate-500">Pilih periode dan filter untuk rekap reservasi lengkap</p>
      </div>

      {/* Mode toggle */}
      <div className="no-print flex flex-wrap gap-2">
        <button data-testid="mode-umum" onClick={() => setMode("umum")}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${mode === "umum" ? "bg-[#7C4DE0] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          <ChartBar size={16} weight="bold" /> Laporan Umum
        </button>
        <button data-testid="mode-imun" onClick={() => setMode("imun")}
          className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition-colors ${mode === "imun" ? "bg-[#DB2777] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
          <Syringe size={16} weight="bold" /> Laporan Imunisasi
        </button>
      </div>

      {/* Filters */}
      <div className="no-print space-y-4 rounded-2xl border border-rose-100/70 bg-white p-5">
        <div className="flex flex-wrap gap-2">
          {[["today", "Hari Ini"], ["yesterday", "Kemarin"], ["week", "Minggu Ini"], ["month", "Bulan Ini"], ["year", "Tahun Ini"], ["custom", "Custom"]].map(([k, l]) => (
            <button key={k} data-testid={`preset-${k}`} onClick={() => setPresetRange(k)}
              className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${preset === k ? "bg-[#7C4DE0] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{l}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Dari Tanggal (Pelayanan)</label>
            <input type="date" data-testid="report-start" value={range[0]} onChange={(e) => { setPreset("custom"); setRange([e.target.value, range[1]]); }} className={inputCls} />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Sampai Tanggal (Pelayanan)</label>
            <input type="date" data-testid="report-end" value={range[1]} onChange={(e) => { setPreset("custom"); setRange([range[0], e.target.value]); }} className={inputCls} />
          </div>

          {mode === "umum" && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Kategori</label>
              <select data-testid="filter-kategori" value={f.kategori} onChange={(e) => setF({ ...f, kategori: e.target.value, jenis_layanan: "" })} className={inputCls}>
                <option value="">Semua Kategori</option>
                {meta.categories.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Layanan</label>
            <select data-testid="filter-layanan" value={f.jenis_layanan} onChange={(e) => setF({ ...f, jenis_layanan: e.target.value })} className={inputCls}>
              <option value="">{mode === "imun" ? "Semua Layanan Imunisasi" : "Semua Layanan"}</option>
              {(mode === "imun" ? imunServices : servicesForCat).map((s) => <option key={s.nama} value={s.nama}>{s.nama}</option>)}
            </select>
          </div>
          {mode === "umum" && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Petugas</label>
              <select data-testid="filter-petugas" value={f.petugas} onChange={(e) => setF({ ...f, petugas: e.target.value })} className={inputCls}>
                <option value="">Semua Petugas</option>
                {meta.petugas.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Jenis Vaksin</label>
            <select data-testid="filter-vaksin" value={f.jenis_vaksin} onChange={(e) => setF({ ...f, jenis_vaksin: e.target.value })} className={inputCls}>
              <option value="">Semua Vaksin</option>
              {meta.vaccines.map((v) => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold text-slate-500">Status Reservasi</label>
            <select data-testid="filter-status" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={inputCls}>
              <option value="">Semua Status</option>
              {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          {mode === "umum" && (
            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-500">Status Pembayaran</label>
              <select data-testid="filter-payment" value={f.payment_status} onChange={(e) => setF({ ...f, payment_status: e.target.value })} className={inputCls}>
                <option value="">Semua</option>
                {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          )}
        </div>
        <button data-testid="report-generate" onClick={load}
          className={`flex items-center justify-center gap-2 rounded-xl px-6 py-2.5 text-sm font-bold text-white active:scale-95 ${mode === "imun" ? "bg-[#DB2777] hover:bg-[#be1e63]" : "bg-[#7C4DE0] hover:bg-[#6A38C7]"}`}>
          <MagnifyingGlass size={18} weight="bold" /> Tampilkan Rekap
        </button>
      </div>

      {loading && (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-rose-200 border-t-rose-500" /></div>
      )}

      {/* ===================== LAPORAN UMUM ===================== */}
      {mode === "umum" && data && !loading && (
        <div className="no-print space-y-5">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {[
              ["total_reservasi", "Jumlah Reservasi", true],
              ["total_pasien", "Jumlah Pasien", false],
              ["lunas", "Lunas", false],
              ["dp", "DP", false],
              ["belum_bayar", "Belum Bayar", false],
              ["total_omzet", "Total Omzet", false],
            ].map(([k, l, accent]) => (
              <div key={k} data-testid={`summary-${k}`} className={`rounded-2xl border p-4 ${accent ? "border-[#7C4DE0] bg-[#EEE6FF]" : "border-rose-100/70 bg-white"}`}>
                <p className="font-heading text-2xl font-extrabold text-slate-800">{k === "total_omzet" ? "Rp" + Number(data.summary[k]).toLocaleString("id-ID") : data.summary[k]}</p>
                <p className="mt-1 text-xs font-medium text-slate-500">{l}</p>
              </div>
            ))}
          </div>

          {perDay.length > 0 && (
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2" data-testid="report-charts">
              <div className="rounded-2xl border border-rose-100/70 bg-white p-4">
                <p className="mb-3 text-sm font-bold text-slate-700">Reservasi per Hari</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={perDay}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3e9f7" vertical={false} />
                    <XAxis dataKey="label" fontSize={11} tickLine={false} />
                    <YAxis allowDecimals={false} fontSize={11} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: "#f7f0ff" }} />
                    <Bar dataKey="value" name="Reservasi" fill="#7C4DE0" radius={[6, 6, 0, 0]} maxBarSize={46} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="rounded-2xl border border-rose-100/70 bg-white p-4">
                <p className="mb-3 text-sm font-bold text-slate-700">Reservasi per Kategori</p>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={perKat} layout="vertical" margin={{ left: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3e9f7" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis type="category" dataKey="label" width={110} fontSize={10} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: "#fdf2f8" }} />
                    <Bar dataKey="value" name="Reservasi" fill="#EC4899" radius={[0, 6, 6, 0]} maxBarSize={26} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-3">
            <button data-testid="export-xlsx" onClick={() => download("xlsx")} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 active:scale-95"><FileXls size={18} weight="duotone" /> Excel</button>
            <button data-testid="export-csv" onClick={() => download("csv")} className="flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-teal-700 active:scale-95"><FileCsv size={18} weight="duotone" /> CSV</button>
            <button data-testid="export-pdf" onClick={() => download("pdf")} className="flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700 active:scale-95"><FilePdf size={18} weight="duotone" /> PDF</button>
            <button data-testid="print-button" onClick={() => window.print()} className="flex items-center gap-2 rounded-xl bg-slate-800 px-4 py-2.5 text-sm font-bold text-white hover:bg-slate-900 active:scale-95"><Printer size={18} weight="duotone" /> Print</button>
          </div>

          {data.items.length === 0 ? (
            <div className="rounded-2xl border border-rose-100/70 bg-white p-10 text-center text-sm text-slate-400" data-testid="report-empty">
              <FunnelSimple size={28} className="mx-auto mb-2 text-slate-300" /> Tidak ada data untuk filter ini.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white" data-testid="report-table">
              <table className="w-full whitespace-nowrap text-sm">
                <thead>
                  <tr className="border-b border-rose-100 bg-[#F7F0FF] text-left text-xs font-bold uppercase text-slate-500">
                    <th className="px-3 py-3">No</th>
                    {columns.map((c) => <th key={c.key} className="px-3 py-3">{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((r, i) => (
                    <tr key={r.id} className="border-b border-rose-50 last:border-0">
                      <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                      {columns.map((c) => (
                        <td key={c.key} className={`px-3 py-2 ${c.key === "nama_lengkap" ? "font-semibold text-slate-800" : "text-slate-600"}`}>{cellVal(r, c.key)}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ===================== LAPORAN IMUNISASI ===================== */}
      {mode === "imun" && imun && !loading && (
        <div className="no-print space-y-5">
          {/* Ringkasan Kebutuhan Vaksin */}
          <div className="rounded-2xl border border-rose-100/70 bg-white p-5" data-testid="imun-vaccine-summary">
            <p className="mb-3 flex items-center gap-1.5 font-heading text-sm font-extrabold text-[#DB2777]"><Syringe size={16} weight="duotone" /> Ringkasan Kebutuhan Vaksin</p>
            {imun.vaccine_summary.length === 0 ? (
              <p className="text-sm text-slate-400">Belum ada permintaan vaksin pada periode ini.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full whitespace-nowrap text-sm">
                  <thead>
                    <tr className="border-b border-rose-100 bg-[#FCE7F3] text-left text-xs font-bold uppercase text-slate-500">
                      <th className="px-3 py-2.5">Vaksin</th>
                      <th className="px-3 py-2.5">Menunggu</th>
                      <th className="px-3 py-2.5">Dikonfirmasi</th>
                      <th className="px-3 py-2.5">Total Aktif</th>
                      <th className="px-3 py-2.5">Dibatalkan</th>
                    </tr>
                  </thead>
                  <tbody>
                    {imun.vaccine_summary.map((s) => (
                      <tr key={s.vaksin} data-testid={`imun-vax-${s.vaksin}`} className="border-b border-rose-50 last:border-0">
                        <td className="px-3 py-2 font-semibold text-slate-800">{s.vaksin}</td>
                        <td className="px-3 py-2 text-amber-600">{s.menunggu}</td>
                        <td className="px-3 py-2 text-emerald-600">{s.dikonfirmasi}</td>
                        <td className="px-3 py-2 font-bold text-[#DB2777]">{s.total_aktif}</td>
                        <td className="px-3 py-2 text-slate-400">{s.dibatalkan}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Export buttons */}
          <div className="flex flex-wrap gap-3">
            <button data-testid="imun-export-xlsx" onClick={() => downloadImun("xlsx")} className="flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700 active:scale-95"><FileXls size={18} weight="duotone" /> Excel</button>
            <button data-testid="imun-export-pdf" onClick={() => downloadImun("pdf")} className="flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700 active:scale-95"><FilePdf size={18} weight="duotone" /> PDF</button>
          </div>

          {imun.items.length === 0 ? (
            <div className="rounded-2xl border border-rose-100/70 bg-white p-10 text-center text-sm text-slate-400" data-testid="imun-empty">
              <FunnelSimple size={28} className="mx-auto mb-2 text-slate-300" /> Tidak ada reservasi imunisasi untuk filter ini.
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-rose-100/70 bg-white" data-testid="imun-table">
              <table className="w-full whitespace-nowrap text-sm">
                <thead>
                  <tr className="border-b border-rose-100 bg-[#FCE7F3] text-left text-xs font-bold uppercase text-slate-500">
                    <th className="px-3 py-3">No</th>
                    {imunColumns.map((c) => <th key={c.key} className="px-3 py-3">{c.label}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {imun.items.map((r, i) => (
                    <tr key={r.id} className="border-b border-rose-50 last:border-0">
                      <td className="px-3 py-2 text-slate-400">{i + 1}</td>
                      {imunColumns.map((c) => (
                        <td key={c.key} className={`px-3 py-2 ${r[c.key] === "Belum diisi" ? "italic text-amber-500" : c.key === "nama_bayi" ? "font-semibold text-slate-800" : "text-slate-600"}`}>{r[c.key]}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Print area (umum only) */}
      {mode === "umum" && data && !loading && data.items.length > 0 && (
        <div className="print-area">
          <div style={{ textAlign: "center", marginBottom: 12 }}>
            <h2 style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>GBS CLINIC &amp; BIRTH CENTER</h2>
            <h3 style={{ fontSize: 13, fontWeight: 700, margin: "2px 0" }}>Laporan Reservasi</h3>
            <p style={{ fontSize: 11, margin: 0 }}>Periode: {formatDateLong(range[0])} s/d {formatDateLong(range[1])}</p>
            {f.kategori && <p style={{ fontSize: 11, margin: 0 }}>Kategori: {f.kategori}</p>}
            {f.jenis_layanan && <p style={{ fontSize: 11, margin: 0 }}>Layanan: {f.jenis_layanan}</p>}
            {f.petugas && <p style={{ fontSize: 11, margin: 0 }}>Petugas: {f.petugas}</p>}
            {f.jenis_vaksin && <p style={{ fontSize: 11, margin: 0 }}>Jenis Vaksin: {f.jenis_vaksin}</p>}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 8 }}>
            <thead>
              <tr>
                <th style={pcell}>No</th>
                {columns.map((c) => <th key={c.key} style={pcell}>{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {data.items.map((r, i) => (
                <tr key={r.id}>
                  <td style={pcell}>{i + 1}</td>
                  {columns.map((c) => <td key={c.key} style={pcell}>{cellVal(r, c.key)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 10, fontSize: 11 }}>
            <p style={{ margin: "2px 0", fontWeight: 700 }}>Jumlah Reservasi: {data.summary.total_reservasi} · Jumlah Pasien: {data.summary.total_pasien}</p>
            <p style={{ margin: "2px 0" }}>Lunas: {data.summary.lunas} · DP: {data.summary.dp} · Belum Bayar: {data.summary.belum_bayar}</p>
            <p style={{ margin: "2px 0", fontWeight: 700 }}>Total Omzet: Rp{Number(data.summary.total_omzet).toLocaleString("id-ID")}</p>
          </div>
        </div>
      )}
    </div>
  );
}

const pcell = { border: "1px solid #333", padding: 3, verticalAlign: "top", textAlign: "left" };
